import { isDeepEqual } from "~/tools/is-deep-equal";
import { useEffect } from "~/tools/use-effect";
import { useHandler } from "~/tools/use-handler";

export interface UseIntersectionOptions extends IntersectionObserverInit {
	/**
	 * If false, disables the observer.
	 *
	 * @default true
	 */
	enabled?: boolean;

	/**
	 * The DOM node to observe.
	 */
	node: null | undefined | HTMLElement;
}

/**
 * React hook that sets up an IntersectionObserver on a given DOM node and invokes a callback
 * whenever the node's intersection status changes.
 *
 * @param callback - Function to be called with the IntersectionObserverEntry when the intersection changes.
 * @param options - Configuration options for the observer.
 *
 * @remarks
 * The hook ensures that the observer is only active when `enabled` is true and the `node` is provided.
 * The observer is properly cleaned up when dependencies change or the component unmounts.
 */
export function useIntersection(
	callback: (entry: IntersectionObserverEntry, node: HTMLElement) => void,
	{ node, enabled = true, root, rootMargin, threshold }: UseIntersectionOptions,
): void {
	const stableCallback = useHandler(callback);
	const enabledNode = enabled ? node : null;

	useEffect(
		() => {
			if (enabledNode == null) {
				return;
			}

			const observer = new IntersectionObserver(([entry]) => stableCallback(entry, enabledNode), {
				root,
				// Firefox reports incorrect isIntersecting to the callback when threshold is at the default 0
				// Specifically for content inside the drawers as they animate from off-screen.
				rootMargin: rootMargin ?? "110%",
				threshold: threshold ?? 0.1,
			});

			observer.observe(enabledNode);

			return () => {
				observer.disconnect();
			};
		},
		[threshold, enabledNode, root, rootMargin, stableCallback],
		[isDeepEqual],
	);
}
